import { useEffect, useRef, useState } from 'react';
import { ImportServicesModal } from './ImportServicesModal';
import { servicesToCsv } from '../../lib/serviceCsv';
import { uniqueServiceName } from '../../lib/serviceNaming';
import {
  archiveService,
  createService,
  updateProfile,
  updateService,
  useCurrentUserProfile,
  useCustomServices,
  type ServiceIcon,
  type ServicePhase,
  type ServiceRow,
} from '@busy/api';
import { CUT_ADDONS, CUT_TYPES, totalTargetSec } from '@busy/core';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';
import { TimePickerWheel } from '../TimePickerWheel';
import { CutPreviewModal } from '../cutTimer/CutPreviewModal';

// Settings → Cut Timer editor panel.
// Mirrors the demo's renderSettingsCutTimer (DEMO_REFERENCE.html:17820) scoped
// for v1: lists the user's custom services + add-ons, with built-in defaults
// shown as a read-only reference section. Each row taps into a full-screen
// edit modal where the user can rename, set price, swap icon, edit phases.
//
// Built-in CUT_TYPES are NOT user-editable here (they live in @busy/core as
// the seed catalog). To customize one, the user duplicates it as a custom
// service via the Duplicate button on each built-in row.

export function CutTimerEditorPanel() {
  const t = useT();
  const toast = useToast();
  const profile = useCurrentUserProfile();
  const services = useCustomServices();
  const [editing, setEditing] = useState<ServiceEditorState | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const seededOnceRef = useRef(false);
  const seededAddonsOnceRef = useRef(false);

  /** Serialize the user's custom services + addons to a CSV file and
   *  trigger a download. The barber AirDrops / emails / Slacks the file
   *  to a coworker, who imports it via the matching Import button. */
  function handleExport() {
    const rows = (services.data ?? []).filter((s) => !s.is_archived);
    if (rows.length === 0) {
      toast(t('settings.cuttimer.export.empty'), 'info');
      return;
    }
    const csv = servicesToCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const a = document.createElement('a');
    a.href = url;
    a.download = `busy-barber-menu-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast(t('settings.cuttimer.export.done', { n: rows.length }), 'success');
  }

  const customs = services.data ?? [];
  const customServices = customs.filter((s) => !s.is_addon);
  const customAddons = customs.filter((s) => s.is_addon);

  // Auto-seed the built-in defaults into the user's custom services on
  // first open of the editor — fixes the cold-start friction where new
  // barbers landed on an empty list and had to manually duplicate every
  // built-in. Two separate gates (cut_types_seeded + cut_addons_seeded)
  // so each kind seeds independently; existing users who opened the
  // editor before migration 0077 still get their add-ons seeded on the
  // next open. Both are once-per-session via ref guards because the
  // profile + services hooks may re-fire while the panel is mounted.
  useEffect(() => {
    if (seededOnceRef.current) return;
    if (!profile.data || services.loading) return;
    if (profile.data.cut_types_seeded) return;
    seededOnceRef.current = true;
    void seedDefaults({ allowDuplicates: false }).then(async () => {
      await updateProfile({ cut_types_seeded: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.data, services.loading]);

  useEffect(() => {
    if (seededAddonsOnceRef.current) return;
    if (!profile.data || services.loading) return;
    if (profile.data.cut_addons_seeded) return;
    seededAddonsOnceRef.current = true;
    void seedAddonDefaults({ allowDuplicates: false }).then(async () => {
      await updateProfile({ cut_addons_seeded: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.data, services.loading]);

  /** Adds the built-in CUT_TYPES to the user's custom services. When
   *  allowDuplicates=false (the default), names that already exist on the
   *  user are skipped — used by the auto-seed AND the Restore button so
   *  Restore is always additive, never destructive. */
  async function seedDefaults({ allowDuplicates }: { allowDuplicates: boolean }) {
    const existingNames = new Set(
      (services.data ?? []).filter((s) => !s.is_addon).map((s) => s.name.trim().toLowerCase()),
    );
    const toAdd = CUT_TYPES.filter((c) => {
      const cutName = c.nameKey ? t(c.nameKey) : c.name;
      return allowDuplicates || !existingNames.has(cutName.trim().toLowerCase());
    });
    for (const c of toAdd) {
      const cutName = c.nameKey ? t(c.nameKey) : c.name;
      await createService({
        name: cutName,
        price_cents: c.priceCents,
        is_addon: false,
        icon: c.icon,
        phases: c.phases.map((p) => ({
          name: p.nameKey ? t(p.nameKey) : p.name,
          sec: p.sec,
          cat: p.cat,
        })),
        duration_min: Math.max(1, Math.round(totalTargetSec(c.phases as never[]) / 60)),
      });
    }
    return toAdd.length;
  }

  /** Mirror of seedDefaults for CUT_ADDONS — same dedupe-by-name shape so
   *  Restore is additive across both cuts and add-ons. Returns the count
   *  added so the toast can summarize either set or both. */
  async function seedAddonDefaults({ allowDuplicates }: { allowDuplicates: boolean }) {
    const existingNames = new Set(
      (services.data ?? []).filter((s) => s.is_addon).map((s) => s.name.trim().toLowerCase()),
    );
    const toAdd = CUT_ADDONS.filter((a) => {
      const addonName = a.nameKey ? t(a.nameKey) : a.name;
      return allowDuplicates || !existingNames.has(addonName.trim().toLowerCase());
    });
    for (const a of toAdd) {
      const addonName = a.nameKey ? t(a.nameKey) : a.name;
      await createService({
        name: addonName,
        price_cents: a.priceCents,
        is_addon: true,
        icon: a.icon,
        phases: a.phases.map((p) => ({
          name: p.nameKey ? t(p.nameKey) : p.name,
          sec: p.sec,
          cat: p.cat,
        })),
        duration_min: Math.max(1, Math.round(totalTargetSec(a.phases as never[]) / 60)),
      });
    }
    return toAdd.length;
  }

  async function handleRestore() {
    const ok = confirm(t('settings.cuttimer.restoreConfirm'));
    if (!ok) return;
    setRestoring(true);
    // Restore both kinds — Restore is additive, so any built-ins the
    // user has already removed and not re-deleted come back; anything
    // they still have stays untouched.
    const cuts = await seedDefaults({ allowDuplicates: false });
    const addons = await seedAddonDefaults({ allowDuplicates: false });
    const added = cuts + addons;
    setRestoring(false);
    if (added === 0) {
      toast(t('settings.cuttimer.restoreNothing'), 'info');
    } else {
      toast(t('settings.cuttimer.restoreDone', { n: added }), 'success');
    }
  }

  /** Archive a row from the inline trash button. Confirm dialog gates
   *  the action — same archiveService used by the in-modal Delete. */
  async function handleRowDelete(row: ServiceRow) {
    const ok = confirm(t('settings.cuttimer.deleteRowConfirm', { name: row.name }));
    if (!ok) return;
    const { error } = await archiveService(row.id);
    if (error) {
      toast(t('cuttimer.edit.error', { message: error.message }), 'error');
    } else {
      toast(t('cuttimer.edit.deleted'), 'success');
    }
  }

  function startNew(isAddon: boolean) {
    setEditing({
      mode: 'create',
      draft: {
        name: '',
        price_cents: 0,
        is_addon: isAddon,
        icon: 'scissors',
        phases: [],
      },
    });
  }

  function startEdit(row: ServiceRow) {
    setEditing({
      mode: 'edit',
      id: row.id,
      draft: {
        name: row.name,
        price_cents: row.price_cents,
        is_addon: row.is_addon,
        icon: row.icon,
        phases: row.phases,
      },
    });
  }

  function startDuplicate(row: { name: string; price_cents: number; phases: ServicePhase[]; icon: ServiceIcon; is_addon?: boolean }) {
    // Auto-number suffix matches iOS Contacts when duplicating a row:
    // "Beard Trim" → "Beard Trim 2" → "Beard Trim 3" — scans the live
    // services list for the lowest free slot. The user can still rename
    // before saving; this just removes the "(copy) collides on save"
    // dead-end the old behavior had.
    const isAddon = row.is_addon ?? false;
    const proposedName = uniqueServiceName(services.data ?? [], isAddon, row.name);
    setEditing({
      mode: 'create',
      draft: {
        name: proposedName,
        price_cents: row.price_cents,
        is_addon: isAddon,
        icon: row.icon,
        phases: row.phases.map((p) => ({ ...p })),
      },
    });
  }

  return (
    <div className="space-y-5">
      {/* Import / Export action row — sits above the menu sections so
          a barber can share their setup with a coworker in two taps.
          CSV format is documented in lib/serviceCsv.ts. */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowImport(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-line text-ink-700 font-semibold text-[13px] hover:bg-bg-alt transition-colors"
        >
          <ImportArrowIcon className="w-3.5 h-3.5" />
          {t('settings.cuttimer.import.button')}
        </button>
        <button
          type="button"
          onClick={handleExport}
          className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-line text-ink-700 font-semibold text-[13px] hover:bg-bg-alt transition-colors"
        >
          <ExportArrowIcon className="w-3.5 h-3.5" />
          {t('settings.cuttimer.export.button')}
        </button>
      </div>

      {/* Custom services section */}
      <section>
        <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-3">
          {t('settings.cuttimer.section.services')}
        </div>
        {customServices.length === 0 ? (
          <p className="text-sm text-ink-500 mb-3">{t('settings.cuttimer.empty')}</p>
        ) : (
          <ul className="space-y-2 mb-3">
            {customServices.map((s) => (
              <ServiceRowItem
                key={s.id}
                row={s}
                onClick={() => startEdit(s)}
                onDelete={() => handleRowDelete(s)}
              />
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() => startNew(false)}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-dashed border-line text-ink-700 font-semibold hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          {t('settings.cuttimer.add')}
        </button>
      </section>

      {/* Custom add-ons */}
      <section>
        <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-3">
          {t('settings.cuttimer.section.addons')}
        </div>
        {customAddons.length > 0 && (
          <ul className="space-y-2 mb-3">
            {customAddons.map((s) => (
              <ServiceRowItem
                key={s.id}
                row={s}
                onClick={() => startEdit(s)}
                onDelete={() => handleRowDelete(s)}
              />
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() => startNew(true)}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-dashed border-line text-ink-700 font-semibold hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          {t('settings.cuttimer.addAddon')}
        </button>
      </section>

      {/* Built-in defaults — read-only reference, with Duplicate to convert into custom */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-3">
          <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
            {t('settings.cuttimer.section.builtins')}
          </span>
          <button
            type="button"
            onClick={handleRestore}
            disabled={restoring}
            className="text-[11px] font-bold tracking-wide uppercase text-navy-900 dark:text-gold-500 hover:underline disabled:opacity-50 inline-flex items-center gap-1"
          >
            <RefreshIcon className={`w-3 h-3 ${restoring ? 'animate-spin' : ''}`} />
            {restoring ? t('settings.cuttimer.restoring') : t('settings.cuttimer.restore')}
          </button>
        </div>
        <p className="text-[11px] text-ink-500 mb-3 leading-snug">
          {t('settings.cuttimer.builtinsNote')}
        </p>
        <ul className="space-y-2">
          {CUT_TYPES.map((c) => {
            const totalMin = Math.round(totalTargetSec(c.phases as never[]) / 60);
            const cutName = c.nameKey ? t(c.nameKey) : c.name;
            return (
              <li
                key={c.id}
                className="rounded-xl border border-line bg-bg-alt p-3 flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-surface grid place-items-center text-ink-700 shrink-0">
                  <PlusIcon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <strong className="block text-sm">{cutName}</strong>
                  <span className="text-xs text-ink-500">
                    {t('settings.cuttimer.minutes', { n: totalMin })} ·{' '}
                    {t(
                      c.phases.length === 1
                        ? 'settings.cuttimer.phaseCount_one'
                        : 'settings.cuttimer.phaseCount_other',
                      { n: c.phases.length },
                    )}
                    {c.priceCents > 0 && ` · $${(c.priceCents / 100).toFixed(0)}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    startDuplicate({
                      name: cutName,
                      price_cents: c.priceCents,
                      icon: c.icon,
                      is_addon: false,
                      phases: c.phases.map((p) => ({
                        name: p.nameKey ? t(p.nameKey) : p.name,
                        sec: p.sec,
                        cat: p.cat,
                      })),
                    })
                  }
                  className="text-xs font-bold tracking-wide text-navy-900 dark:text-gold-500 hover:underline shrink-0"
                >
                  {t('settings.cuttimer.duplicate')}
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {editing && <ServiceEditorModal state={editing} onClose={() => setEditing(null)} />}
      {showImport && <ImportServicesModal onClose={() => setShowImport(false)} />}
    </div>
  );
}

function ImportArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function ExportArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

/* ============================================================================
 * Row in the list view
 * ========================================================================== */

function ServiceRowItem({
  row,
  onClick,
  onDelete,
}: {
  row: ServiceRow;
  onClick: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const totalSec = row.phases.reduce((s, p) => s + p.sec, 0);
  const totalMin = Math.round(totalSec / 60);
  // $/hr — the rate the cut earns per chair-hour. Helps barbers price by
  // economics, not feel. Only computed for cuts (not add-ons) with a
  // duration + a price, so the badge doesn't show on free or zero-time
  // services where the math is meaningless.
  const hourlyRate =
    !row.is_addon && totalSec > 0 && row.price_cents > 0
      ? Math.round((row.price_cents / 100) * (3600 / totalSec))
      : null;
  return (
    <li className="rounded-xl border border-line bg-surface flex items-stretch gap-2 hover:border-navy-900 dark:hover:border-gold-500 transition-colors overflow-hidden">
      <button
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 text-left p-3 flex items-center gap-3"
      >
        <div className="w-10 h-10 rounded-lg bg-bg-alt grid place-items-center text-navy-900 dark:text-gold-500 shrink-0">
          <ServiceIconGlyph icon={row.icon} className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <strong className="text-sm">{row.name}</strong>
            {hourlyRate !== null && (
              <span
                className="text-[10px] tracking-[0.06em] uppercase font-extrabold text-gold-700 dark:text-gold-400 bg-gold-500/15 py-0.5 pr-1.5 rounded-full tabular-nums"
                title={t('settings.cuttimer.rateTooltip')}
                style={{ paddingLeft: 'calc(0.375rem + 0.06em)' }}
              >
                ${hourlyRate}/hr
              </span>
            )}
          </div>
          <span className="text-xs text-ink-500">
            {row.phases.length > 0 ? (
              <>
                {t('settings.cuttimer.minutes', { n: totalMin })} ·{' '}
                {t(
                  row.phases.length === 1
                    ? 'settings.cuttimer.phaseCount_one'
                    : 'settings.cuttimer.phaseCount_other',
                  { n: row.phases.length },
                )}
                {row.price_cents > 0 && ` · $${(row.price_cents / 100).toFixed(0)}`}
              </>
            ) : (
              t('settings.cuttimer.noPhases')
            )}
          </span>
          {row.phases.length > 0 && totalSec > 0 && (
            <PhaseTimeBar phases={row.phases} totalSec={totalSec} className="mt-1.5" />
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-ink-500 shrink-0" />
      </button>
      {/* Inline delete affordance — discoverable trash icon next to each
          row so removing a service doesn't require opening the editor
          and scrolling to the bottom. Confirm dialog gates the destructive
          action. */}
      <button
        type="button"
        onClick={onDelete}
        aria-label={t('settings.cuttimer.deleteRowAria', { name: row.name })}
        className="shrink-0 w-12 grid place-items-center text-ink-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-600/15 transition-colors border-l border-line"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </li>
  );
}

/** Horizontal stacked bar visualizing a cut's phase shape. Each phase is
 *  a colored block sized proportionally to its duration. Hover/tap shows
 *  the phase name + duration via title attribute (kept simple — not a
 *  full tooltip so it stays cheap). Used in the row list AND the editor
 *  modal for the same visual language. */
function PhaseTimeBar({
  phases,
  totalSec,
  className,
}: {
  phases: ServicePhase[];
  totalSec: number;
  className?: string;
}) {
  const t = useT();
  return (
    <div className={`flex h-2 rounded-full overflow-hidden bg-bg-alt ${className ?? ''}`}>
      {phases.map((p, i) => {
        const pct = totalSec > 0 ? (p.sec / totalSec) * 100 : 0;
        if (pct <= 0) return null;
        return (
          <div
            key={i}
            className={CATEGORY_COLORS[p.cat] ?? CATEGORY_COLORS.setup}
            style={{ width: `${pct}%` }}
            title={`${p.name || t('cuttimer.edit.phaseName')} · ${formatPhaseTime(p.sec)}`}
          />
        );
      })}
    </div>
  );
}

const CATEGORY_COLORS: Record<string, string> = {
  setup: 'bg-blue-400',
  bulk: 'bg-navy-700',
  fade: 'bg-navy-900',
  lineup: 'bg-red-500',
  finish: 'bg-gold-500',
  beard: 'bg-amber-700',
  hottowel: 'bg-cyan-500',
  shave: 'bg-slate-500',
  cut: 'bg-navy-800',
  single: 'bg-navy-900',
};

function formatPhaseTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/* ============================================================================
 * Edit modal — name + icon + price + phases list (add/remove/reorder)
 * ========================================================================== */

type EditDraft = {
  name: string;
  price_cents: number;
  is_addon: boolean;
  icon: ServiceIcon;
  phases: ServicePhase[];
};

type ServiceEditorState =
  | { mode: 'create'; draft: EditDraft }
  | { mode: 'edit'; id: string; draft: EditDraft };

const ICONS: ServiceIcon[] = ['scissors', 'kit', 'razor', 'baby', 'bolt'];
const CATEGORIES = ['setup', 'bulk', 'fade', 'lineup', 'finish', 'beard', 'hottowel', 'shave'] as const;

// Common phase templates — tapping a chip in the editor inserts a phase
// pre-filled with the canonical name + a sensible default duration +
// the right category. Removes the "what should I call this phase?"
// friction for newer barbers and lets experienced ones drop in a
// well-formed phase in one tap.
const PHASE_TEMPLATES: ReadonlyArray<{ labelKey: string; sec: number; cat: typeof CATEGORIES[number] }> = [
  { labelKey: 'cuttimer.template.setup',     sec: 90,  cat: 'setup' },
  { labelKey: 'cuttimer.template.hottowel',  sec: 90,  cat: 'hottowel' },
  { labelKey: 'cuttimer.template.bulk',      sec: 720, cat: 'bulk' },
  { labelKey: 'cuttimer.template.fade',      sec: 480, cat: 'fade' },
  { labelKey: 'cuttimer.template.lineup',    sec: 240, cat: 'lineup' },
  { labelKey: 'cuttimer.template.beard',     sec: 180, cat: 'beard' },
  { labelKey: 'cuttimer.template.detail',    sec: 180, cat: 'finish' },
  { labelKey: 'cuttimer.template.style',     sec: 120, cat: 'finish' },
];

function ServiceEditorModal({
  state,
  onClose,
}: {
  state: ServiceEditorState;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [draft, setDraft] = useState<EditDraft>(state.draft);
  const [saving, setSaving] = useState(false);
  // Open phase index for the scroll-wheel time picker. -1 = closed.
  const [pickerForIdx, setPickerForIdx] = useState<number>(-1);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isCreate = state.mode === 'create';
  const titleKey = isCreate
    ? draft.is_addon
      ? 'cuttimer.edit.titleNewAddon'
      : 'cuttimer.edit.titleNew'
    : draft.is_addon
    ? 'cuttimer.edit.titleEditAddon'
    : 'cuttimer.edit.titleEdit';

  const totalSec = draft.phases.reduce((s, p) => s + p.sec, 0);

  function patch<K extends keyof EditDraft>(key: K, val: EditDraft[K]) {
    setDraft((d) => ({ ...d, [key]: val }));
  }

  function patchPhase(idx: number, val: Partial<ServicePhase>) {
    setDraft((d) => ({
      ...d,
      phases: d.phases.map((p, i) => (i === idx ? { ...p, ...val } : p)),
    }));
  }

  function addPhase() {
    setDraft((d) => ({
      ...d,
      phases: [...d.phases, { name: '', sec: 60, cat: 'setup' }],
    }));
  }

  /** Drop a templated phase into the draft. Used by the chip strip above
   *  the "+ Add phase" button — one tap inserts a well-formed phase. */
  function addTemplatedPhase(tpl: (typeof PHASE_TEMPLATES)[number]) {
    setDraft((d) => ({
      ...d,
      phases: [...d.phases, { name: t(tpl.labelKey), sec: tpl.sec, cat: tpl.cat }],
    }));
  }

  // Single-phase toggle. Senior barbers often run a flat timer (no
  // setup/cut/finish breakdown), so we let them collapse to one big
  // phase. Derived from the phase list — single-phase = exactly one
  // phase with cat='single'. Toggling collapses the multi-phase list
  // into one (preserving total time) or expands the single phase
  // back to a three-phase default starter set.
  const isSinglePhase = draft.phases.length === 1 && draft.phases[0]?.cat === 'single';

  function toggleSinglePhase() {
    setDraft((d) => {
      const totalSec = d.phases.reduce((s, p) => s + p.sec, 0);
      const wasSingle = d.phases.length === 1 && d.phases[0]?.cat === 'single';
      if (wasSingle) {
        // Expand back to a three-phase starter so the barber can edit.
        const total = totalSec || 1500; // 25 min fallback
        const setup = Math.max(60, Math.round(total * 0.15));
        const cut = Math.max(60, Math.round(total * 0.65));
        const finish = Math.max(60, total - setup - cut);
        return {
          ...d,
          phases: [
            { name: 'Setup', sec: setup, cat: 'setup' },
            { name: 'Cut', sec: cut, cat: 'cut' },
            { name: 'Finish', sec: finish, cat: 'finish' },
          ],
        };
      }
      // Collapse to one big phase, preserving total time.
      return {
        ...d,
        phases: [{ name: 'Cut', sec: Math.max(60, totalSec || 1500), cat: 'single' }],
      };
    });
  }

  function removePhase(idx: number) {
    setDraft((d) => ({
      ...d,
      phases: d.phases.filter((_, i) => i !== idx),
    }));
  }

  function movePhase(idx: number, dir: -1 | 1) {
    setDraft((d) => {
      const next = [...d.phases];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return d;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return { ...d, phases: next };
    });
  }

  async function handleSave() {
    if (!draft.name.trim()) {
      toast(t('cuttimer.edit.nameRequired'), 'error');
      return;
    }
    setSaving(true);
    const payload: Partial<ServiceRow> = {
      name: draft.name.trim(),
      price_cents: draft.price_cents,
      is_addon: draft.is_addon,
      icon: draft.icon,
      phases: draft.phases,
      duration_min: Math.max(1, Math.round(totalSec / 60)),
    };
    const result = isCreate
      ? await createService({ ...payload, name: draft.name.trim(), phases: draft.phases })
      : await updateService(state.id, payload);
    setSaving(false);
    if (result.error) {
      toast(t('cuttimer.edit.error', { message: result.error.message }), 'error');
      return;
    }
    toast(t(isCreate ? 'cuttimer.edit.created' : 'cuttimer.edit.saved'), 'success');
    onClose();
  }

  async function handleDelete() {
    if (state.mode !== 'edit') return;
    if (!confirm(t('cuttimer.edit.deleteConfirm'))) return;
    const { error } = await archiveService(state.id);
    if (error) {
      toast(t('cuttimer.edit.error', { message: error.message }), 'error');
      return;
    }
    toast(t('cuttimer.edit.deleted'), 'success');
    onClose();
  }

  return (
    <ModalShell open onClose={onClose} size="lg" ariaLabel={t('cuttimer.edit.aria')}>
      {/* flex-col + max-h frame so the Save/Delete footer stays pinned at
          the bottom of the modal no matter how many phases the user adds.
          Was a real pain on the old layout — phase 8+ pushed Save below
          the fold on phones. */}
      <div className="flex flex-col max-h-[90dvh]">
      <ModalCloseButton onClose={onClose} />
      <div className="shrink-0 border-b border-line p-5 pr-12">
        <h2 className="font-display text-2xl">{t(titleKey)}</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5 min-h-0">
        <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
          <div>
            <label className="block text-xs font-bold text-ink-500 mb-1.5">
              {t('cuttimer.edit.name')}
            </label>
            <input
              type="text"
              autoFocus
              value={draft.name}
              onChange={(e) => patch('name', e.target.value)}
              placeholder={t('cuttimer.edit.namePh')}
              className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-ink-500 mb-1.5">
              {t('cuttimer.edit.price')}
            </label>
            <input
              type="number"
              min="0"
              step="1"
              inputMode="decimal"
              value={(draft.price_cents / 100).toString()}
              onChange={(e) => {
                const dollars = parseFloat(e.target.value);
                patch('price_cents', Number.isFinite(dollars) ? Math.round(dollars * 100) : 0);
              }}
              className="w-28 h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 tabular-nums focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
            />
          </div>
        </div>

        {/* Icon picker */}
        <div>
          <label className="block text-xs font-bold text-ink-500 mb-2">
            {t('cuttimer.edit.icon')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {ICONS.map((ic) => {
              const selected = draft.icon === ic;
              return (
                <button
                  key={ic}
                  type="button"
                  onClick={() => patch('icon', ic)}
                  className={`w-12 h-12 rounded-xl grid place-items-center transition-colors ${
                    selected
                      ? 'bg-navy-900 text-gold-400 dark:bg-gold-500 dark:text-navy-900'
                      : 'bg-bg-alt text-ink-700 border border-line-strong hover:border-navy-700'
                  }`}
                  aria-label={ic}
                >
                  <ServiceIconGlyph icon={ic} className="w-5 h-5" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Add-on toggle (only useful when creating; on existing, switching shifts where it appears) */}
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-3 cursor-pointer">
          <div className="min-w-0 flex-1">
            <strong className="block text-sm">{t('cuttimer.edit.isAddon')}</strong>
            <span className="text-xs text-ink-500 leading-snug">{t('cuttimer.edit.isAddonHelp')}</span>
          </div>
          <span
            className={`relative inline-block w-10 h-6 rounded-full transition-colors shrink-0 ${
              draft.is_addon ? 'bg-navy-900 dark:bg-gold-500' : 'bg-line-strong'
            }`}
          >
            <input
              type="checkbox"
              checked={draft.is_addon}
              onChange={(e) => patch('is_addon', e.target.checked)}
              className="absolute opacity-0 w-full h-full cursor-pointer"
            />
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                draft.is_addon ? 'translate-x-4' : ''
              }`}
            />
          </span>
        </label>

        {/* Use-phases toggle. ON shows the per-phase editor (default).
            OFF collapses to a single big "Cut" phase with one duration —
            for senior barbers who don't want phase-by-phase coaching. */}
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-3 cursor-pointer">
          <div className="min-w-0 flex-1">
            <strong className="block text-sm">{t('cuttimer.edit.usePhases')}</strong>
            <span className="text-xs text-ink-500 leading-snug">{t('cuttimer.edit.usePhasesHelp')}</span>
          </div>
          <span
            className={`relative inline-block w-10 h-6 rounded-full transition-colors shrink-0 ${
              !isSinglePhase ? 'bg-navy-900 dark:bg-gold-500' : 'bg-line-strong'
            }`}
          >
            <input
              type="checkbox"
              checked={!isSinglePhase}
              onChange={toggleSinglePhase}
              className="absolute opacity-0 w-full h-full cursor-pointer"
            />
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                !isSinglePhase ? 'translate-x-4' : ''
              }`}
            />
          </span>
        </label>

        {isSinglePhase ? (
          // Flat-timer mode — one duration field. Tap to open the
          // scroll-wheel picker; no name/category to fuss with.
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
                {t('cuttimer.edit.totalDurationHeader')}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setPickerForIdx(0)}
              className="w-full rounded-2xl border border-line bg-surface p-5 grid place-items-center hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
            >
              <div className="font-display text-4xl tabular-nums text-navy-900 dark:text-gold-400">
                {fmtMMSS(draft.phases[0]?.sec ?? 0)}
              </div>
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-ink-500 mt-1">
                {t('cuttimer.edit.tapToEdit')}
              </div>
            </button>
          </div>
        ) : (
          /* Phase list */
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
                {t('cuttimer.edit.phasesHeader')}
              </span>
              <span className="text-[11px] tracking-[0.12em] uppercase font-bold text-ink-500 tabular-nums">
                {t('cuttimer.edit.totalLabel')} {fmtMMSS(totalSec)}
              </span>
            </div>
            {/* Visual time-bar — same component as the row view, so the
                editor + the list speak the same visual language. Lets the
                barber see the cut's shape change as they tweak. */}
            {draft.phases.length > 0 && totalSec > 0 && (
              <PhaseTimeBar phases={draft.phases} totalSec={totalSec} className="mb-3" />
            )}
            <ul className="space-y-2">
              {draft.phases.map((p, i) => (
                <li
                  key={i}
                  className="rounded-xl border border-line bg-surface p-3 flex items-center gap-2"
                >
                  <span className="w-6 h-6 rounded-full bg-bg-alt text-ink-500 grid place-items-center text-[11px] font-bold tabular-nums shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0 grid sm:grid-cols-[1fr_120px_120px] gap-2">
                    <input
                      type="text"
                      value={p.name}
                      onChange={(e) => patchPhase(i, { name: e.target.value })}
                      placeholder={t('cuttimer.edit.phaseName')}
                      className="h-9 px-2 rounded-lg border-[1.5px] border-line-strong bg-surface text-ink-900 text-sm focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
                    />
                    {/* Duration is a button that opens the iOS-style
                        scroll-wheel picker. Way less awful on touch than
                        the previous text input that popped the keyboard. */}
                    <button
                      type="button"
                      onClick={() => setPickerForIdx(i)}
                      className="h-9 px-2 rounded-lg border-[1.5px] border-line-strong bg-surface text-ink-900 text-sm tabular-nums hover:border-navy-700 dark:hover:border-gold-500 inline-flex items-center justify-center gap-1.5"
                    >
                      <ClockIcon className="w-3 h-3 text-ink-500" />
                      {fmtMMSS(p.sec)}
                    </button>
                    <select
                      value={p.cat}
                      onChange={(e) => patchPhase(i, { cat: e.target.value })}
                      className="h-9 px-2 rounded-lg border-[1.5px] border-line-strong bg-surface text-ink-900 text-sm focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {t(`cat.phase.${c}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => movePhase(i, -1)}
                      disabled={i === 0}
                      aria-label={t('cuttimer.edit.movePhaseUp')}
                      className="w-7 h-7 rounded grid place-items-center text-ink-500 hover:bg-bg-alt disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => movePhase(i, 1)}
                      disabled={i === draft.phases.length - 1}
                      aria-label={t('cuttimer.edit.movePhaseDown')}
                      className="w-7 h-7 rounded grid place-items-center text-ink-500 hover:bg-bg-alt disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePhase(i)}
                    aria-label={t('cuttimer.edit.removePhase')}
                    className="w-7 h-7 rounded grid place-items-center text-ink-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-600/15 shrink-0"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
            {/* Phase template chips — one-tap insert for the most common
                phase shapes, pre-filled with name + duration + category. */}
            <div className="mt-3">
              <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 mb-1.5">
                {t('cuttimer.edit.quickAddHeader')}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {PHASE_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.labelKey}
                    type="button"
                    onClick={() => addTemplatedPhase(tpl)}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-line bg-bg-alt text-xs font-bold text-ink-700 hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${CATEGORY_COLORS[tpl.cat] ?? 'bg-ink-500'}`}
                      aria-hidden
                    />
                    {t(tpl.labelKey)}
                    <span className="text-ink-500 tabular-nums">{formatPhaseTime(tpl.sec)}</span>
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={addPhase}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 h-10 rounded-lg border-2 border-dashed border-line text-ink-700 font-semibold text-sm hover:border-navy-900 dark:hover:border-gold-500"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              {t('cuttimer.edit.addPhaseBlank')}
            </button>
          </div>
        )}

        <TimePickerWheel
          open={pickerForIdx >= 0}
          totalSec={pickerForIdx >= 0 ? draft.phases[pickerForIdx]?.sec ?? 0 : 0}
          title={
            pickerForIdx >= 0
              ? draft.phases[pickerForIdx]?.name || t('cuttimer.edit.phaseDuration')
              : undefined
          }
          onConfirm={(sec) => {
            patchPhase(pickerForIdx, { sec });
            setPickerForIdx(-1);
          }}
          onClose={() => setPickerForIdx(-1)}
        />

        {state.mode === 'edit' && (
          <button
            type="button"
            onClick={handleDelete}
            className="text-xs text-red-600 font-semibold hover:underline"
          >
            {t('cuttimer.edit.delete')}
          </button>
        )}
      </div>

      <div className="shrink-0 border-t border-line bg-bg-alt p-4 space-y-2">
        {/* Preview / test-run — runs the current draft in the cut timer
            UI at 4× speed without saving a visit. Lets the barber feel
            the rhythm of their phase design before relying on it on a
            real client. Disabled when there are no phases or zero total
            time (nothing to preview). */}
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          disabled={draft.phases.length === 0 || draft.phases.every((p) => p.sec === 0)}
          className="w-full h-11 rounded-lg border-2 border-navy-900 dark:border-gold-500 text-navy-900 dark:text-gold-500 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <PlayIcon className="w-4 h-4" />
          {t('cuttimer.edit.preview')}
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold disabled:opacity-50"
          >
            {saving
              ? t('cuttimer.edit.saving')
              : isCreate
              ? t('cuttimer.edit.create')
              : t('cuttimer.edit.save')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 h-11 rounded-lg border border-line bg-surface font-semibold text-ink-700"
          >
            {t('cuttimer.edit.cancel')}
          </button>
        </div>
      </div>
      </div>
      {previewOpen && (
        <CutPreviewModal
          cutName={draft.name || t('cuttimer.edit.namePh')}
          phases={draft.phases}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </ModalShell>
  );
}

/* ============================================================================
 * Helpers
 * ========================================================================== */

function fmtMMSS(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// Accepts "2:30" or "2" (treat as minutes) or "150" (treat as seconds if >59).

/* ============================================================================
 * Icons
 * ========================================================================== */

function ServiceIconGlyph({ icon, className }: { icon: ServiceIcon; className?: string }) {
  const props = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (icon) {
    case 'scissors':
      return (
        <svg {...props}>
          <circle cx="6" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <line x1="20" y1="4" x2="8.12" y2="15.88" />
          <line x1="14.47" y1="14.48" x2="20" y2="20" />
          <line x1="8.12" y1="8.12" x2="12" y2="12" />
        </svg>
      );
    case 'kit':
      return (
        <svg {...props}>
          <rect x="3" y="7" width="18" height="13" rx="2" />
          <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      );
    case 'razor':
      return (
        <svg {...props}>
          <path d="M12 2v6" />
          <rect x="6" y="8" width="12" height="6" rx="1" />
          <path d="M8 14v6" />
          <path d="M16 14v6" />
        </svg>
      );
    case 'baby':
      return (
        <svg {...props}>
          <path d="M9 12h.01" />
          <path d="M15 12h.01" />
          <path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" />
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
    case 'bolt':
      return (
        <svg {...props}>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
  }
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="6 4 20 12 6 20 6 4" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronUp({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 15 12 9 18 15" />
    </svg>
  );
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}
